import { ComponentProps, useMemo } from 'react';
import { integrationHealthIsoStateIds } from '@constants';
import type { DatesSetArg } from '@fullcalendar/core';
import debounce from 'lodash/debounce';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useAppDispatch } from 'redux/hooks';
import { fetchIntegrationHealth } from 'thunk';
import {
  formatIntegrationHealthDateParams,
  renderIntegrationHealthStatus,
  renderIntegrationHealthByRequestAndSuccessCountPercent,
  isIntegrationHealthy,
} from 'utils';
import { Typography, Box, Tooltip } from '@mui/material';
import { integrationHealthIsoStateModifiers } from 'IntegrationModule/utils';
import useIntegrationHealthData from './useIntegrationHealthData';
import useAppNavigation from 'hooks/useAppNavigation';
import SimpleCalendar from 'components/SimpleCalendar';
import { IntegrationHealth } from 'types';
import { StyledAlert } from 'shared/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Alert } from '@mui/material';

type Props = {
  targetService: string;
  targetServiceId: number;
  teamId: number;
};

type LegendEntry = {
  severity: ComponentProps<typeof Alert>['severity'];
  label: string;
};

// Mirrors the thresholds in renderIntegrationHealthByRequestAndSuccessCountPercent
// (utils/display.tsx). The legend below only describes the four success-rate
// buckets that map to MUI Alert severities — auth-failed / rate-limited /
// inactive / not-configured render distinct icons (Lock, TimerOff, PauseCircle,
// LinkOff) above the calendar grid and aren't represented here. Conflating
// auth failure into the Critical bucket label would mislead a reader who sees
// the Lock icon and looks for it in the legend.
const footerIcons: LegendEntry[] = [
  { severity: 'success', label: 'Healthy — 95-100% success' },
  { severity: 'info', label: 'Good — 80-94% success' },
  { severity: 'warning', label: 'Degraded — 50-79% success' },
  { severity: 'error', label: 'Critical — <50% success' },
];

const isoStateId = integrationHealthIsoStateIds.IntegrationHealthCalendar;

const emptyArr: never[] = [];

const getFormattedEvents = ({
  targetService,
  targetServiceId,
  data,
}: {
  targetService: string;
  targetServiceId: number;
  data: {
    [targetService: string]: {
      [targetServiceId: number]: IntegrationHealth[];
    };
  };
}) => {
  const integrationHealthDataByTargetService = data[targetService]?.[targetServiceId] || emptyArr;

  return integrationHealthDataByTargetService.map((item) => {
    const date = formatIntegrationHealthDateParams(toZonedTime(parseISO(item.date), 'UTC'));
    return {
      id: date,
      key: date,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      start: date,
      extendedProps: {
        item,
      },
    };
  });
};

const useIntegrationHealthCalendar = ({ targetService, targetServiceId, teamId }: Props) => {
  const dispatch = useAppDispatch();

  const { data } = useIntegrationHealthData({ isoStateId });

  const { navigateToIntegrationHealth } = useAppNavigation();

  const onIntegrationHealthDateChange = useMemo(
    () =>
      debounce((arg: DatesSetArg) => {
        const { startStr, endStr } = arg;
        const startDate = formatIntegrationHealthDateParams(parseISO(startStr));
        const endDate = formatIntegrationHealthDateParams(parseISO(endStr));
        dispatch(
          fetchIntegrationHealth({
            targetService,
            targetServiceIds: [targetServiceId],
            serviceNames: [targetService],
            startDate,
            endDate,
            isoStateId,
            isInitialFetch: true,
            modifier: integrationHealthIsoStateModifiers[isoStateId],
          }),
        );
      }, 500),
    [dispatch, targetService, targetServiceId],
  );

  const renderLegendIcons = () => {
    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        {footerIcons.map((entry) => (
          <Box
            key={entry.severity}
            component={'p'}
            p={'0px 12px'}
            margin={'3px 0px'}
            fontSize={'10px'}
          >
            <StyledAlert
              className="icon-only inline-block remove-icon-padding remove-message-padding"
              sx={{ position: 'relative', top: 6, left: 6 }}
              severity={entry.severity}
            />{' '}
            {entry.label}
          </Box>
        ))}
      </Box>
    );
  };

  const renderHeader = () => {
    return (
      <Box component={'h3'} p={'12px'} margin={'10px 0px'}>
        Integration Health
        <Tooltip placement="top-start" title={renderLegendIcons()}>
          <HelpOutlineIcon />
        </Tooltip>
      </Box>
    );
  };

  const IntegrationHealthCalendar = (
    <SimpleCalendar
      events={getFormattedEvents({ targetService, targetServiceId, data })}
      headerToolBar={{
        left: 'prev,next',
        center: 'title',
        right: 'today',
      }}
      renderHeader={renderHeader}
      datesSet={onIntegrationHealthDateChange}
      eventContent={(arg) => {
        const { item } = arg.event.extendedProps;
        return (
          <>
            {renderIntegrationHealthByRequestAndSuccessCountPercent({
              integrationHealth: item,
              onClick: () =>
                navigateToIntegrationHealth({
                  targetService,
                  targetServiceId,
                  teamId,
                }),
              className: 'icon-only no-margin-on-icon',
            })}
          </>
        );
      }}
    />
  );

  return {
    IntegrationHealthCalendar,
  };
};

export default useIntegrationHealthCalendar;
